import { type ReactNode, type Ref, useId, useState } from 'react'
import { tableCopy } from '@/copy'
import type { SurfaceWidthToken } from '@/styles/tokens'
import { cx } from '../cx'
import { Checkbox } from '../Field/Field'
import { Icon } from '../Icon/Icon'
import { Skeleton } from '../Loading/Loading'
import styles from './Table.module.css'

export type TableColumn<Row> = {
  key: string
  header: string
  align?: 'start' | 'end'
  width?: SurfaceWidthToken
  sortable?: boolean
  render?: (row: Row) => ReactNode
}

export type TableSort = {
  key: string
  direction: 'ascending' | 'descending'
}

const LOADING_ROWS = 3
const loadingRowKeys = Array.from({ length: LOADING_ROWS }, (_, i) => i)

export function Table<Row>({
  caption,
  columns,
  rows,
  getRowId,
  sort,
  onSort,
  selection,
  onSelectionChange,
  onRowClick,
  density = 'comfortable',
  loading,
  error,
  empty,
  footer,
  ref,
  className,
}: {
  caption: string
  columns: readonly TableColumn<Row>[]
  rows: readonly Row[]
  getRowId: (row: Row) => string
  sort?: TableSort
  onSort?: (next: TableSort) => void
  selection?: ReadonlySet<string>
  onSelectionChange?: (next: Set<string>) => void
  onRowClick?: (row: Row) => void
  density?: 'comfortable' | 'compact'
  loading?: boolean
  error?: ReactNode
  empty?: ReactNode
  footer?: Partial<Record<string, ReactNode>>
  ref?: Ref<HTMLDivElement>
  className?: string
}) {
  const captionId = useId()
  const selectable = Boolean(selection && onSelectionChange)
  const span = columns.length + (selectable ? 1 : 0)

  const sortKey = sort?.key
  const sortDirection = sort?.direction
  // Derived during render (not in an effect): a sort that differs from the
  // last one seen is announced; the sort present on mount is not.
  const [seen, setSeen] = useState({ key: sortKey, direction: sortDirection })
  const [announcement, setAnnouncement] = useState('')
  if (seen.key !== sortKey || seen.direction !== sortDirection) {
    setSeen({ key: sortKey, direction: sortDirection })
    if (sortKey !== undefined && sortDirection !== undefined) {
      const header = columns.find((c) => c.key === sortKey)?.header ?? sortKey
      setAnnouncement(tableCopy.sorted(header, sortDirection))
    }
  }

  const nextSort = (key: string): TableSort => ({
    key,
    direction:
      sort?.key === key && sort.direction === 'ascending'
        ? 'descending'
        : 'ascending',
  })

  const selectedCount = selection
    ? rows.filter((r) => selection.has(getRowId(r))).length
    : 0
  const allSelected = rows.length > 0 && selectedCount === rows.length
  const someSelected = selectedCount > 0 && !allSelected

  const toggleAll = () => {
    if (!selection || !onSelectionChange) return
    const next = new Set(selection)
    for (const r of rows) {
      if (allSelected) next.delete(getRowId(r))
      else next.add(getRowId(r))
    }
    onSelectionChange(next)
  }

  const toggleRow = (id: string) => {
    if (!selection || !onSelectionChange) return
    const next = new Set(selection)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    onSelectionChange(next)
  }

  const messageRow = (content: ReactNode) => (
    <tr>
      <td colSpan={span} className={styles.message}>
        {content}
      </td>
    </tr>
  )

  let body: ReactNode
  if (loading) {
    body = loadingRowKeys.map((n) => (
      <tr key={n}>
        {selectable && <td className={styles.select} />}
        {columns.map((c) => (
          <td key={c.key} data-align={c.align ?? 'start'}>
            <Skeleton variant="text" />
          </td>
        ))}
      </tr>
    ))
  } else if (error) {
    body = messageRow(error)
  } else if (rows.length === 0) {
    body = messageRow(empty)
  } else {
    body = rows.map((row) => {
      const id = getRowId(row)
      const firstCellId = `${captionId}-${id}`
      return (
        <tr
          key={id}
          data-activatable={onRowClick ? '' : undefined}
          onClick={
            onRowClick
              ? (e) => {
                  if (
                    (e.target as Element).closest(
                      'a, button, input, select, textarea, label',
                    )
                  )
                    return
                  onRowClick(row)
                }
              : undefined
          }
        >
          {selectable && (
            <td className={styles.select}>
              <Checkbox
                checked={selection?.has(id) ?? false}
                onChange={() => toggleRow(id)}
                aria-labelledby={firstCellId}
              />
            </td>
          )}
          {columns.map((c, i) => (
            <td
              key={c.key}
              id={i === 0 ? firstCellId : undefined}
              data-align={c.align ?? 'start'}
            >
              {c.render
                ? c.render(row)
                : String((row as Record<string, unknown>)[c.key] ?? '')}
            </td>
          ))}
        </tr>
      )
    })
  }

  return (
    <div className={cx(styles.root, className)}>
      {/* biome-ignore lint/a11y/useSemanticElements: the scroller is a named region over a table, not a <section> */}
      <div
        ref={ref}
        className={styles.scroller}
        role="region"
        aria-labelledby={captionId}
        // biome-ignore lint/a11y/noNoninteractiveTabindex: a scrollable region must be keyboard-focusable to scroll
        tabIndex={0}
      >
        <table
          className={styles.table}
          data-density={density}
          aria-busy={loading || undefined}
        >
          <caption id={captionId} className={styles.visuallyHidden}>
            {caption}
          </caption>
          <colgroup>
            {selectable && <col className={styles.selectCol} />}
            {columns.map((c) => (
              <col key={c.key} data-width={c.width} />
            ))}
          </colgroup>
          <thead>
            <tr>
              {selectable && (
                <th scope="col" className={styles.select}>
                  <Checkbox
                    checked={allSelected}
                    indeterminate={someSelected}
                    onChange={toggleAll}
                    aria-label={tableCopy.selectAll}
                  />
                </th>
              )}
              {columns.map((c) => (
                <th
                  key={c.key}
                  scope="col"
                  data-align={c.align ?? 'start'}
                  aria-sort={sort?.key === c.key ? sort.direction : undefined}
                >
                  {c.sortable ? (
                    <button
                      type="button"
                      className={styles.sort}
                      data-active={sort?.key === c.key || undefined}
                      onClick={() => onSort?.(nextSort(c.key))}
                    >
                      {c.header}
                      <Icon name="caret-sort" size="icon-embedded" />
                    </button>
                  ) : (
                    c.header
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>{body}</tbody>
          {footer && (
            <tfoot>
              <tr>
                {selectable && <td className={styles.select} />}
                {columns.map((c) => (
                  <td key={c.key} data-align={c.align ?? 'start'}>
                    {footer[c.key]}
                  </td>
                ))}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      <span role="status" className={styles.visuallyHidden}>
        {announcement}
      </span>
    </div>
  )
}
